import { useMutation, useQuery } from "@tanstack/react-query";
import type { ApiErrorCode, ExportColumn, ExportFieldKey, ErrorEnvelope } from "@guestbook/shared";
import { apiFetch, ApiClientError, refreshAccessToken } from "@/shared/lib/api";

const API_BASE = "/api/v1";

/**
 * Human-readable labels for every selectable export field (drives the
 * dropdown). Only contact-owned fields are selectable; the household's name
 * and address override these at export time when a contact belongs to one.
 * Labels mirror the import feature's CONTACT_FIELDS for consistency.
 */
export const FIELD_LABELS: Record<ExportFieldKey, string> = {
  "contact.name": "Name",
  "contact.email": "Email",
  "contact.phone": "Phone",
  "contact.notes": "Notes",
  "contact.address.line1": "Address line 1",
  "contact.address.line2": "Address line 2",
  "contact.address.city": "City",
  "contact.address.region": "State / region",
  "contact.address.postalCode": "ZIP",
  "contact.address.country": "Country",
};

/** All field keys in display order — derived from the label map. */
export const FIELD_KEYS = Object.keys(FIELD_LABELS) as ExportFieldKey[];

/** UTF-8-safe base64url encode (matches the API's Buffer.from(_, "base64url")). */
function toBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as { error: unknown }).error === "object"
  );
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="?([^"]+)"?/.exec(header);
  return match?.[1] ?? null;
}

export interface ExportCsvParams {
  columns: ExportColumn[];
  /** The active list scope, or null for all contacts. */
  listId: string | null;
}

/** The `/contacts/export` or `/lists/:id/export` path for a scope. */
function exportPath(listId: string | null): string {
  return listId ? `/lists/${encodeURIComponent(listId)}/export` : "/contacts/export";
}

/**
 * Downloads a CSV for the given scope + column config. Uses a raw fetch
 * (not apiFetch, which only parses JSON) to read the blob, then triggers a
 * browser download via a temporary anchor. Retries once after a refresh on a
 * 401 so a stale access cookie doesn't drop the download.
 */
async function downloadCsv({ columns, listId }: ExportCsvParams): Promise<void> {
  const url = `${API_BASE}${exportPath(listId)}?columns=${toBase64Url(JSON.stringify(columns))}`;

  const request = () =>
    fetch(url, { credentials: "include", headers: { Accept: "text/csv" } });

  let res: Response;
  try {
    res = await request();
    if (res.status === 401 && (await refreshAccessToken())) {
      res = await request();
    }
  } catch (err) {
    throw new ApiClientError(
      "network",
      err instanceof Error ? err.message : "Network error",
      0,
    );
  }

  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      // non-JSON error body — fall through to a generic message
    }
    if (isErrorEnvelope(payload)) {
      throw new ApiClientError(
        payload.error.code as ApiErrorCode,
        payload.error.message,
        res.status,
        payload.error.details,
      );
    }
    throw new ApiClientError("unknown", res.statusText || "Export failed", res.status);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download =
    filenameFromDisposition(res.headers.get("Content-Disposition")) ?? "contacts.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export function useExportCsv() {
  return useMutation<void, ApiClientError, ExportCsvParams>({
    mutationFn: downloadCsv,
  });
}

/** Resolved sample of the first records in scope, for the on-page preview. */
export interface ExportPreview {
  headers: string[];
  rows: string[][];
}

/**
 * Fetches a resolved preview (headers + the first few records' values) for the
 * given column config and scope, so the page can show what the CSV will look
 * like — including household overrides — before downloading. JSON, so it goes
 * through apiFetch (unlike the blob download). Callers should pass a debounced
 * `columns` so header-rename keystrokes don't spam the endpoint.
 */
export function useExportPreview({ columns, listId }: ExportCsvParams) {
  const encoded = toBase64Url(JSON.stringify(columns));
  return useQuery<ExportPreview, ApiClientError>({
    queryKey: ["export-preview", listId, encoded],
    queryFn: () =>
      apiFetch<ExportPreview>(`${exportPath(listId)}?preview=1&columns=${encoded}`),
    enabled: columns.some((c) => c.visible),
  });
}
